import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    applyActionCode,
    verifyPasswordResetCode,
    confirmPasswordReset,
    reload,
} from "firebase/auth";
import { auth } from "../../firebase/firebase";
import { useAuth } from "../../firebase/AuthContext";
import { useAnalytics } from "../../firebase/hooks/useAnalytics";
import { redirectTarget } from "../../firebase/redirect";
import rightSide from "../../assets/newHome.jpg";

// Where the links in Firebase's auth emails land, in place of Firebase's own
// English-only handler on firebaseapp.com. It is set as the custom action URL
// under Authentication → Templates, and Firebase appends
// ?mode=…&oobCode=…&lang=… to it. That URL is one setting shared by every
// template, so a mode this app never sends (recoverEmail, verifyAndChangeEmail)
// still arrives here and gets an "invalid link" message instead of a blank page.

const SUPPORTED_MODES = ["verifyEmail", "resetPassword"];

// Firebase's own minimum. Checked here only so a short password is flagged
// before the round trip, not after.
const MIN_PASSWORD_LENGTH = 6;

function errorMessage(code, isEN) {
    switch (code) {
        case "auth/expired-action-code":
            return isEN ? "This link has expired." : "此連結已過期。";
        case "auth/invalid-action-code":
            return isEN
                ? "This link is invalid or has already been used."
                : "此連結無效或已被使用。";
        case "auth/user-disabled":
            return isEN ? "This account has been disabled." : "此帳號已被停用。";
        case "auth/user-not-found":
            return isEN ? "This account no longer exists." : "此帳號已不存在。";
        case "unsupported":
            return isEN ? "This link is invalid." : "此連結無效。";
        default:
            return isEN
                ? "Something went wrong. Please try again."
                : "發生錯誤，請重試。";
    }
}

export default function AuthAction() {
    const navigate = useNavigate();
    const location = useLocation();
    const { trackPageView } = useAnalytics();
    const { currentUser, loading, autoInitializeUser, refreshClaims } =
        useAuth();

    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");
    const oobCode = params.get("oobCode");
    // Firebase sends a BCP 47 tag such as "zh-TW"; the switcher below writes
    // the app's own "zh". Both start the same way.
    const language = (params.get("lang") || "").toLowerCase().startsWith("zh")
        ? "zh"
        : "en";
    const isEN = language === "en";

    // working | verified | resetForm | resetDone | error
    const [status, setStatus] = useState("working");
    const [errorCode, setErrorCode] = useState("");
    const [readyToContinue, setReadyToContinue] = useState(false);
    const [accountEmail, setAccountEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [formError, setFormError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // An action code works exactly once. Running the effect below a second
    // time would get "invalid" back and replace a success with an error.
    const handled = useRef(false);

    useEffect(() => {
        trackPageView("Auth Action");
    }, [trackPageView]);

    useEffect(() => {
        // Wait for any saved session to load first: whether this browser is
        // signed in decides where the verify-email success sends people.
        if (loading || handled.current) return;
        handled.current = true;

        const fail = (code) => {
            setErrorCode(code);
            setStatus("error");
        };

        if (!oobCode || !SUPPORTED_MODES.includes(mode)) {
            fail("unsupported");
            return;
        }

        if (mode === "resetPassword") {
            verifyPasswordResetCode(auth, oobCode)
                .then((email) => {
                    setAccountEmail(email);
                    setStatus("resetForm");
                })
                .catch((err) => fail(err.code));
            return;
        }

        const verifyEmail = async () => {
            const user = auth.currentUser;
            try {
                await applyActionCode(auth, oobCode);
            } catch (err) {
                // A second click on the same link: the code is spent, but if
                // this browser's session already shows the address verified,
                // the visitor has what they came for.
                if (user) await reload(user).catch(() => {});
                if (!user?.emailVerified) {
                    fail(err.code);
                    return;
                }
            }

            if (user) {
                // Usually the signup tab's session. Do the setup that
                // /verify-email's "I've verified" button would have done, so
                // the visitor can go straight in from here.
                try {
                    await reload(user);
                    if (user.emailVerified) {
                        await autoInitializeUser(user);
                        await refreshClaims();
                        setReadyToContinue(true);
                    }
                } catch (err) {
                    // The address is verified either way. Logging in runs the
                    // same setup, so the visitor just takes that route instead.
                    console.error("Post-verification setup failed:", err);
                }
            }
            setStatus("verified");
        };
        verifyEmail();
    }, [loading, mode, oobCode, autoInitializeUser, refreshClaims]);

    const setLanguageInUrl = (lang) => {
        const next = new URLSearchParams(location.search);
        next.set("lang", lang);
        navigate({ search: next.toString() }, { replace: true });
    };

    const goTo = (pathname) =>
        navigate({ pathname, search: `?lang=${language}` }, { replace: true });

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        setFormError("");

        if (password.length < MIN_PASSWORD_LENGTH) {
            setFormError(
                isEN
                    ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
                    : `密碼至少需要 ${MIN_PASSWORD_LENGTH} 個字元。`,
            );
            return;
        }
        if (password !== confirmPassword) {
            setFormError(isEN ? "Passwords do not match." : "兩次輸入的密碼不一致。");
            return;
        }

        setSubmitting(true);
        try {
            await confirmPasswordReset(auth, oobCode, password);
            setStatus("resetDone");
        } catch (err) {
            if (err.code === "auth/weak-password") {
                setFormError(
                    isEN
                        ? "Password is too weak. Please choose a stronger one."
                        : "密碼強度不足，請選擇更強的密碼。",
                );
            } else if (err.code === "auth/password-does-not-meet-requirements") {
                // Only reachable if a password policy is switched on in the
                // console. Firebase lists the unmet rules in brackets, in English.
                const unmet = err.message.match(/\[(.*)\]/)?.[1];
                setFormError(
                    (isEN
                        ? "Password does not meet the requirements"
                        : "密碼不符合要求") + (unmet ? `: ${unmet}` : "."),
                );
            } else {
                setErrorCode(err.code);
                setStatus("error");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const statusIcon = (ok) => (
        <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                ok ? "bg-green-100" : "bg-red-100"
            }`}
        >
            <svg
                className={`w-8 h-8 ${ok ? "text-green-600" : "text-red-600"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={ok ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"}
                ></path>
            </svg>
        </div>
    );

    const primaryButton = (label, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className="w-full p-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-md mt-6 transition-colors duration-200"
        >
            {label}
        </button>
    );

    const renderContent = () => {
        if (status === "working") {
            return (
                <div className="text-center py-8">
                    <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">
                        {mode === "resetPassword"
                            ? isEN
                                ? "Checking your link..."
                                : "正在檢查連結..."
                            : isEN
                              ? "Verifying your email..."
                              : "正在驗證您的電子郵件..."}
                    </p>
                </div>
            );
        }

        if (status === "verified") {
            return (
                <div className="text-center">
                    {statusIcon(true)}
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {isEN ? "Email Verified" : "電子郵件已驗證"}
                    </h1>
                    <p className="text-gray-600">
                        {readyToContinue
                            ? isEN
                                ? "Your email address has been verified. You're all set."
                                : "您的電子郵件地址已驗證，一切就緒。"
                            : isEN
                              ? "Your email address has been verified. You can now log in."
                              : "您的電子郵件地址已驗證，現在可以登入。"}
                    </p>
                    {readyToContinue
                        ? primaryButton(isEN ? "Continue" : "繼續", () =>
                              navigate(redirectTarget(location.search, language), {
                                  replace: true,
                              }),
                          )
                        : primaryButton(isEN ? "Log in" : "登入", () =>
                              goTo("/login"),
                          )}
                </div>
            );
        }

        if (status === "resetForm") {
            return (
                <>
                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            {isEN ? "Reset Password" : "重置密碼"}
                        </h1>
                        <p className="text-gray-600 mb-1">
                            {isEN
                                ? "Choose a new password for"
                                : "請為以下帳號設定新密碼"}
                        </p>
                        <p className="text-sm font-medium text-orange-600">
                            {accountEmail}
                        </p>
                    </div>

                    <form
                        onSubmit={handleResetSubmit}
                        className="flex flex-col space-y-4"
                    >
                        <label className="text-gray-700 font-medium">
                            {isEN ? "New password" : "新密碼"}
                        </label>
                        <input
                            type="password"
                            autoComplete="new-password"
                            placeholder={
                                isEN ? "Enter a new password" : "請輸入新密碼"
                            }
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />

                        <label className="text-gray-700 font-medium">
                            {isEN ? "Confirm new password" : "確認新密碼"}
                        </label>
                        <input
                            type="password"
                            autoComplete="new-password"
                            placeholder={
                                isEN
                                    ? "Re-enter the new password"
                                    : "請再次輸入新密碼"
                            }
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />

                        {formError && (
                            <p className="text-red-600 text-sm">* {formError}</p>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full p-3 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold rounded-md mt-5 transition-colors duration-200"
                        >
                            {submitting
                                ? isEN
                                    ? "Saving..."
                                    : "儲存中..."
                                : isEN
                                  ? "Save New Password"
                                  : "儲存新密碼"}
                        </button>
                    </form>
                </>
            );
        }

        if (status === "resetDone") {
            return (
                <div className="text-center">
                    {statusIcon(true)}
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {isEN ? "Password Updated" : "密碼已更新"}
                    </h1>
                    <p className="text-gray-600">
                        {isEN
                            ? "You can now log in with your new password."
                            : "您現在可以使用新密碼登入。"}
                    </p>
                    {primaryButton(isEN ? "Log in" : "登入", () =>
                        goTo("/login"),
                    )}
                </div>
            );
        }

        // status === "error"
        const title =
            mode === "resetPassword"
                ? isEN
                    ? "Couldn't Reset Password"
                    : "無法重置密碼"
                : mode === "verifyEmail"
                  ? isEN
                      ? "Couldn't Verify Email"
                      : "無法驗證電子郵件"
                  : isEN
                    ? "Invalid Link"
                    : "連結無效";

        let action;
        if (mode === "resetPassword") {
            action = primaryButton(
                isEN ? "Request a New Link" : "重新取得連結",
                () => goTo("/forgot-password"),
            );
        } else if (mode === "verifyEmail" && currentUser) {
            // /verify-email has the resend button and its cooldown.
            action = primaryButton(
                isEN ? "Resend Verification Email" : "重新發送驗證郵件",
                () => goTo("/verify-email"),
            );
        } else {
            action = primaryButton(isEN ? "Back to Login" : "返回登入", () =>
                goTo("/login"),
            );
        }

        return (
            <div className="text-center">
                {statusIcon(false)}
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
                <p className="text-gray-600">{errorMessage(errorCode, isEN)}</p>
                {action}
            </div>
        );
    };

    return (
        <div className="lg:flex h-screen font-sans relative">
            {/* Left Panel */}
            <div className="flex justify-center items-center lg:bg-slate-100 lg:w-1/2 relative z-10 min-h-screen lg:min-h-0">
                <div className="w-11/12 sm:w-3/4 lg:w-3/4 p-6 sm:p-8 bg-white/95 lg:bg-slate-100 backdrop-blur-sm lg:backdrop-blur-none rounded-xl lg:rounded-lg shadow-xl lg:shadow-none">
                    {/* Language Switcher */}
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => setLanguageInUrl("en")}
                            className={`text-sm ml-2 px-2 py-1 rounded transition-all duration-200 ${
                                language === "en"
                                    ? "bg-orange-600 text-white"
                                    : "text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            English
                        </button>
                        <button
                            onClick={() => setLanguageInUrl("zh")}
                            className={`text-sm ml-2 px-2 py-1 rounded transition-all duration-200 ${
                                language === "zh"
                                    ? "bg-orange-600 text-white"
                                    : "text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            繁體中文
                        </button>
                    </div>

                    {/* Logo. Absolute src: this page lives one level down at
                        /auth/action, where a relative path would miss. */}
                    <div
                        onClick={() =>
                            navigate({
                                pathname: "/",
                                search: `?lang=${language}`,
                            })
                        }
                        className="flex items-center gap-6 mb-8"
                    >
                        <img
                            src="/logo_tglobal.png"
                            alt="T-Global"
                            className="w-48 h-auto"
                        />
                    </div>

                    {renderContent()}
                </div>
            </div>

            {/* Right Panel */}
            <div className="lg:hidden absolute inset-0 z-0">
                <img
                    src={rightSide}
                    alt=""
                    className="w-full h-full object-cover"
                />
                {/* Dark overlay for better text readability */}
                <div className="absolute inset-0 bg-black/40"></div>
            </div>

            {/* Right Panel */}
            <div className="lg:w-1/2 hidden lg:block flex-1 bg-slate-100 overflow-hidden relative p-8">
                <img
                    src={rightSide}
                    alt=""
                    className="w-full h-full object-cover rounded-xl "
                />
            </div>
        </div>
    );
}
